/**
 * Frontend logger.
 *
 * Development  : all levels printed to the browser console.
 * Production   : log/info/debug are suppressed; warn and error are
 *                printed AND queued for remote submission to the backend
 *                so they end up in the server log files (logs/).
 *
 * Usage:
 *   import { logger } from '../utils/logger';
 *   logger.log('plain info');
 *   logger.warn('something unexpected');
 *   logger.error('something broke', error);
 */

const isDev = import.meta.env.DEV;
const API_URL = import.meta.env.VITE_API_URL || '';

// ── Remote log queue (production only) ───────────────────────────────────────

let queue = [];
let flushTimer = null;

const serialize = (args) =>
  args
    .map((a) => {
      if (a instanceof Error) return `${a.message} — ${a.stack || ''}`;
      if (typeof a === 'object' && a !== null) {
        try { return JSON.stringify(a); } catch { return String(a); }
      }
      return String(a);
    })
    .join(' ');

const enqueue = (level, args) => {
  // Avoid logging auth tokens or objects that may contain them
  queue.push({
    level,
    message: serialize(args).substring(0, 1000), // cap at 1 kB per entry
    timestamp: new Date().toISOString(),
    url: window.location.pathname,
  });

  if (!flushTimer) {
    flushTimer = setTimeout(flush, 3000); // batch window of 3 s
  }
};

const flush = async () => {
  flushTimer = null;
  if (queue.length === 0) return;

  const entries = queue.splice(0, 50); // max 50 per batch

  try {
    // Use fetch directly to avoid depending on the axios api.js module
    // and to allow logging even before auth is set up.
    await fetch(`${API_URL}/api/client-logs`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ entries }),
      keepalive: true, // survives page unload
    });
  } catch {
    // Remote logging is best-effort; never throw
  }
};

// Flush before the page unloads so errors on navigation are not lost
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flush);
}

// ── Public logger API ─────────────────────────────────────────────────────────

export const logger = {
  /** Debug / informational — dev only, never sent to server */
  log: (...args) => {
    if (isDev) console.log(...args);
  },

  /** Alias of log */
  info: (...args) => {
    if (isDev) console.info(...args);
  },

  /** Debug — dev only */
  debug: (...args) => {
    if (isDev) console.debug(...args);
  },

  /** Warning — always shown in dev; in prod sent to server log files */
  warn: (...args) => {
    if (isDev) {
      console.warn(...args);
    } else {
      enqueue('warn', args);
    }
  },

  /** Error — always shown in console; in prod also sent to server log files */
  error: (...args) => {
    console.error(...args);
    if (!isDev) enqueue('error', args);
  },
};
